import React from 'react';
import { Loader2 } from 'lucide-react';

export const SettingToggle: React.FC<{
    label: string;
    description: string;
    checked: boolean;
    onChange: () => void;
    disabled: boolean;
    readOnly: boolean;
}> = ({ label, description, checked, onChange, disabled, readOnly }) => (
    <div className={`flex items-center justify-between gap-2 p-4 bg-gray-50 rounded-2xl border border-gray-100 ${readOnly ? 'opacity-60' : ''}`}>
        <div>
            <p className="font-semibold text-gray-800 text-sm">{label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
        <button
            type="button"
            onClick={onChange}
            disabled={readOnly || disabled}
            className={`relative w-12 h-6 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed ${checked ? 'bg-clip-500' : 'bg-gray-300'}`}
        >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
        </button>
    </div>
);

export const SettingNumberInput: React.FC<{
    label: string;
    description: string;
    value: string | number;
    min?: number;
    max?: number;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    disabled: boolean;
    readOnly: boolean;
    isSaving: boolean;
}> = ({ label, description, value, min, max, onChange, onBlur, disabled, readOnly, isSaving }) => (
    <div className={`flex items-center justify-between gap-2 p-4 bg-gray-50 rounded-2xl border border-gray-100 ${readOnly ? 'opacity-60' : ''}`}>
        <div className="flex-1 mr-4">
            <p className="font-semibold text-gray-800 text-sm">{label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={readOnly || disabled}
                className="w-16 p-2 text-center bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-bold text-gray-800 disabled:opacity-60"
            />
            {isSaving && <Loader2 size={16} className="animate-spin text-gray-400" />}
        </div>
    </div>
);

export const SettingTextarea: React.FC<{
    label: string;
    description: string;
    value: string;
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur: () => void;
    disabled: boolean;
    readOnly: boolean;
    isSaving: boolean;
}> = ({ label, description, value, placeholder, onChange, onBlur, disabled, readOnly, isSaving }) => (
    <div className={`flex flex-col gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 ${readOnly ? 'opacity-60' : ''}`}>
        <div>
            <p className="font-semibold text-gray-800 text-sm">{label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
        <div className="relative">
            <textarea
                rows={3}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={readOnly || disabled}
                placeholder={placeholder}
                className="w-full pl-4 pr-10 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none text-sm text-gray-800 disabled:opacity-60 resize-none"
            />
            {isSaving && (
                <div className="absolute right-3 top-3">
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                </div>
            )}
        </div>
    </div>
);
